import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { ScanLine } from 'lucide-react';
import { useState } from 'react';

import { QrScanner } from '../../../../shared/ui/QrScanner';
import { SearchInput } from '../../../search/ui/components/SearchInput';
import { PageHeader } from '../../../shell/ui/PageHeader';
import { useStationLookup } from '../../hooks/use-station-lookup';
import { useStationSession } from '../../hooks/use-station-session';
import { StationResultList } from '../components/StationResultList';
import { StationSetupForm } from '../components/StationSetupForm';

/**
 * Standalone /station route (design D6) — gated by the station's own
 * session, NOT the operator `_authenticated` guard (spec "no personal valet
 * login required"). Read-only: manual query input + optional scan-to-lookup,
 * scoped server-side to the device's own location. No mutation UI anywhere
 * on this page (StationResultList structurally cannot render one — see its
 * own doc comment).
 */
export function StationPage() {
  const { t } = useTranslation('station');
  const { deviceToken, session, loading, error, submitDeviceToken, forgetDevice, retry } =
    useStationSession();

  // The hook exposes a translatable key, never the raw (English) backend message.
  const errorMessage = error ? t(`errors.${error}`) : null;

  const [query, setQuery] = useState('');
  const [scanning, setScanning] = useState(false);
  const { data, isFetching } = useStationLookup(session?.accessToken ?? null, query);
  const results = data?.results ?? [];

  function handleScanDecode(text: string) {
    setScanning(false);
    setQuery(text);
  }

  // ── Step 1: no device paired yet — one-time setup screen ──────────────────
  if (!deviceToken) {
    return (
      <div className="flex min-h-screen flex-col justify-center gap-4 p-6">
        <StationSetupForm
          submitting={loading}
          errorMessage={errorMessage}
          onSubmit={submitDeviceToken}
        />
      </div>
    );
  }

  // ── Step 2: device paired but no valid session yet — loading/error state ──
  if (!session) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
        {loading ? (
          <p className="jq-body-sm text-muted-foreground">{t('connecting')}</p>
        ) : (
          <>
            <p role="alert" className="font-sans text-sm text-destructive">
              {error === 'invalid_station_token'
                ? t('errors.invalid_station_token_stored')
                : (errorMessage ?? t('sessionError'))}
            </p>
            <div className="flex gap-2">
              <Button type="button" onClick={retry}>
                {t('retry')}
              </Button>
              <Button type="button" variant="outline" onClick={forgetDevice}>
                {t('setup.forget')}
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── Step 3: authenticated station session — read-only lookup UI ───────────
  return (
    <div className="flex flex-col gap-4 p-4 pb-24">
      <PageHeader
        title={t('title')}
        actions={
          <Button type="button" variant="ghost" size="sm" onClick={forgetDevice}>
            {t('setup.forget')}
          </Button>
        }
      />

      {scanning ? (
        <QrScanner
          label={t('scanLabel')}
          onDecode={handleScanDecode}
          onCancel={() => setScanning(false)}
        />
      ) : (
        <div className="flex gap-2">
          <div className="flex-1">
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder={t('searchInputPlaceholder')}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            className="h-12 px-3"
            onClick={() => setScanning(true)}
            aria-label={t('scanAria')}
          >
            <ScanLine className="size-4" />
          </Button>
        </div>
      )}

      {isFetching ? (
        <p className="py-8 text-center font-sans text-sm text-muted-foreground">{t('searching')}</p>
      ) : (
        <StationResultList items={results} query={query} />
      )}
    </div>
  );
}
